"use client";

import { useLayoutEffect, useRef } from "react";

import type { KundaliField } from "@/lib/analytics-kundali";
import { belowStickyChromeClearancePx } from "@/lib/sticky-chrome-offsets";

/**
 * How far to scroll so the field sits clear of the sticky chrome, or 0 when it
 * already does. The clearance is the result reserves' scroll margin: the
 * header's bottom plus air on phones, and header + panchang strip from sm.
 */
function offsetIntoClearView(element: HTMLElement): number {
  const rect = element.getBoundingClientRect();
  // No box (not laid out): there is nothing to bring into view.
  if (rect.width === 0 && rect.height === 0) return 0;
  const clearance = belowStickyChromeClearancePx();
  const clear = rect.top >= clearance && rect.bottom <= window.innerHeight;
  return clear ? 0 : rect.top - clearance;
}

/** Put the caret after text handed over from the shell (text-like inputs only). */
function moveCaretToEnd(element: HTMLElement): void {
  if (!(element instanceof HTMLInputElement) || !element.value) return;
  try {
    element.setSelectionRange(element.value.length, element.value.length);
  } catch {
    // date/time inputs do not support selection ranges
  }
}

/**
 * Focus the real field the seeker opened from the server shell, then bring it
 * clear of the sticky header if it is not already. On desktop /ai-astrologer/
 * a Space in the read-only shell had scrolled the page ~860 px first, and a
 * no-scroll focus stranded the empty field at y=-404, off-screen (C08). A
 * field under the header (top 0-100) counts as on screen to the browser, so
 * a plain focus() would not scroll it either; the scroll is explicit.
 */
export function useInitialKundaliFieldFocus(field: KundaliField | undefined, ready: boolean) {
  const focusedField = useRef<KundaliField | null>(null);

  useLayoutEffect(() => {
    if (!ready || !field || focusedField.current === field) return;
    const input = document.getElementById(field);
    if (!(input instanceof HTMLElement)) return;
    const offset = offsetIntoClearView(input);
    input.focus({ preventScroll: true });
    if (offset !== 0) window.scrollBy({ top: offset });
    moveCaretToEnd(input);
    focusedField.current = field;
  }, [field, ready]);
}
